import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, getActiveClubName, getActiveClubLogo } from "../lib/api";
import { Trophy, ListOrdered, Globe, CalendarClock, Printer } from "lucide-react";

/**
 * TablePage - drei Tabs:
 *  - Spieltag: standings nur für einen ausgewählten Matchday (inkl. Gäste)
 *  - Saison: nur Stammspieler, alle Ranked-Matches der Saison
 *  - Ewig: nur Stammspieler-Matches über alle Saisons hinweg
 */
export default function TablePage() {
  const [tab, setTab] = useState("season"); // matchday | season | eternal
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [matchdays, setMatchdays] = useState([]);
  const [matchdayId, setMatchdayId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const list = await api.listSeasons();
      setSeasons(list);
      const active = list.find((s) => s.active) || list[0];
      if (active) setSeasonId(active.id);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!seasonId) { setMatchdays([]); return; }
    api.listMatchdays(seasonId).then((mds) => {
      setMatchdays(mds);
      const generated = mds.filter((m) => m.generated);
      if (generated.length) setMatchdayId(generated[generated.length - 1].id);
      else if (mds.length) setMatchdayId(mds[0].id);
    });
  }, [seasonId]);

  useEffect(() => {
    if (tab === "eternal") {
      api.tableEternal().then(setRows).catch(() => setRows([]));
    } else if (tab === "season" && seasonId) {
      api.tableSeason(seasonId).then(setRows).catch(() => setRows([]));
    } else if (tab === "matchday" && matchdayId) {
      api.tableMatchday(matchdayId).then(setRows).catch(() => setRows([]));
    } else {
      setRows([]);
    }
  }, [tab, seasonId, matchdayId]);

  const titleMap = { matchday: "Spieltagstabelle", season: "Saisontabelle", eternal: "Ewige Tabelle" };
  const iconMap = { matchday: CalendarClock, season: Trophy, eternal: Globe };
  const Icon = iconMap[tab];
  const season = seasons.find((item) => item.id === seasonId);
  const matchday = matchdays.find((item) => item.id === matchdayId);
  const clubLogo = getActiveClubLogo();
  const printLabel = tab === "matchday" ? `${season?.name || "Saison"} · Spieltag ${matchday?.number || ""}` : tab === "season" ? season?.name : "Alle Saisons";

  return (
    <>
      <style>{`
        @page { size: A4; margin: 13mm; }
        @media print {
          html, body, #root, .App, .App > *, .App > * > * { height: auto !important; min-height: 0 !important; }
          body { background: white !important; color: #111 !important; }
          body::before, header, footer, .no-print { display: none !important; }
          main { padding: 0 !important; margin: 0 !important; min-height: 0 !important; }
          .standings-print { max-width: 100% !important; color: #111 !important; }
          .standings-print * { color: #111 !important; border-color: #999 !important; }
          .standings-print thead { background: #f3f3f3 !important; }
          .standings-print .print-muted { color: #555 !important; }
        }
      `}</style>
    <div className="space-y-6 standings-print" data-testid="table-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-4">
          {clubLogo && <img src={clubLogo} alt={`${getActiveClubName()} Logo`} className="w-16 h-16 object-contain dc-border p-1" style={{ background: "#fff" }} />}
          <div>
          <div className="text-xs uppercase tracking-[0.35em] print-muted" style={{ color: "var(--dc-primary)" }}>{getActiveClubName()}</div>
          <div className="text-xs uppercase tracking-[0.4em]" style={{ color: "var(--dc-chalk)" }}>
            <Icon size={12} className="inline mr-2" /> {titleMap[tab]}
          </div>
          <h1 className="font-display text-5xl">Standings</h1>
          <div className="text-sm mt-1 print-muted" style={{ color: "var(--dc-muted)" }}>{printLabel}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          {tab === "matchday" && matchdayId && <Link to={`/spieltag-bericht/${matchdayId}`} className="dc-btn dc-btn-ghost" data-testid="table-matchday-report-link"><Printer size={14} /> Spielbericht mit Ergebnissen</Link>}
          <button className="dc-btn" onClick={() => window.print()} data-testid="table-print-button"><Printer size={14} /> Nur Tabelle drucken</button>
        </div>
      </div>

      <div className="flex gap-2 border-b dc-border overflow-x-auto no-print">
        <button className={`dc-tab flex items-center gap-2 whitespace-nowrap ${tab === "matchday" ? "active" : ""}`} onClick={() => setTab("matchday")} data-testid="table-tab-matchday">
          <CalendarClock size={14} /> Spieltag
        </button>
        <button className={`dc-tab flex items-center gap-2 whitespace-nowrap ${tab === "season" ? "active" : ""}`} onClick={() => setTab("season")} data-testid="table-tab-season">
          <Trophy size={14} /> Saison
        </button>
        <button className={`dc-tab flex items-center gap-2 whitespace-nowrap ${tab === "eternal" ? "active" : ""}`} onClick={() => setTab("eternal")} data-testid="table-tab-eternal">
          <Globe size={14} /> Ewige Tabelle
        </button>
      </div>

      {tab !== "eternal" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 no-print">
          <div>
            <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Saison</label>
            <select className="dc-input" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} data-testid="table-season-select">
              {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}{s.active ? " (aktiv)" : ""}</option>)}
            </select>
          </div>
          {tab === "matchday" && (
            <div>
              <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Spieltag</label>
              <select className="dc-input" value={matchdayId} onChange={(e) => setMatchdayId(e.target.value)} data-testid="table-matchday-select">
                <option value="">-- wählen --</option>
                {matchdays.map((m) => (
                  <option key={m.id} value={m.id}>
                    Spieltag {m.number}{m.date ? ` · ${m.date}` : ""}{m.generated ? " · gespielt" : " · offen"}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <StandingsTable rows={rows} tab={tab} />
      <p className="text-xs" style={{ color: "var(--dc-muted)" }}>
        {tab === "matchday" && "Nur Matches dieses Spieltags. Gäste werden mitgezählt."}
        {tab === "season" && "Alle gespielten Partien dieser Saison zählen. Gäste erscheinen, sobald sie ein Spiel absolviert haben."}
        {tab === "eternal" && "Aggregiert über alle Saisons – jede gespielte Partie zählt, auch mit Gästen."}
      </p>
    </div>
    </>
  );
}

function StandingsTable({ rows, tab }) {
  const hasHistoricDraws = rows.some((row) => (row.draws || 0) > 0);
  return (
    <div className="dc-border overflow-x-auto">
      <table className="w-full font-mono-data text-sm">
        <thead style={{ background: "var(--dc-surface)" }}>
          <tr className="text-xs uppercase" style={{ color: "var(--dc-muted)" }}>
            <th className="text-left py-3 px-4 w-10">#</th>
            <th className="text-left px-4">Spieler</th>
            <th className="text-right px-3">Sp</th>
            <th className="text-right px-3">S</th>
            {hasHistoricDraws && <th className="text-right px-3">U</th>}
            <th className="text-right px-3">N</th>
            <th className="text-right px-3">Legs+</th>
            <th className="text-right px-3">Legs−</th>
            <th className="text-right px-3">±</th>
            <th className="text-right px-3">Avg</th>
            <th className="text-right px-3">F-Ø</th>
            <th className="text-right px-3">HF</th>
            <th className="text-right px-4">Pkt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.player_id} className="row-in border-t dc-border hover:bg-[color:var(--dc-surface-hover)]"
              style={{ animationDelay: `${i * 30}ms` }} data-testid={`table-row-${r.player_id}`}>
              <td className="py-3 px-4">
                <span className="inline-block w-7 text-center font-display" style={{
                  background: i === 0 ? "var(--dc-primary)" : i < 3 ? "var(--dc-secondary)" : "transparent",
                  color: i < 3 ? "white" : "inherit",
                }}>{r.rank}</span>
              </td>
              <td className="px-4" style={{ fontFamily: "Manrope" }}>
                <Link to={`/spieler/${r.player_id}`} className="hover:underline">{r.name}</Link>
              </td>
              <td className="text-right px-3">{r.played}</td>
              <td className="text-right px-3" style={{ color: "var(--dc-secondary)" }}>{r.wins}</td>
              {hasHistoricDraws && <td className="text-right px-3">{r.draws || 0}</td>}
              <td className="text-right px-3" style={{ color: "var(--dc-primary)" }}>{r.losses}</td>
              <td className="text-right px-3">{r.legs_for}</td>
              <td className="text-right px-3">{r.legs_against}</td>
              <td className="text-right px-3">{r.leg_diff > 0 ? "+" : ""}{r.leg_diff}</td>
              <td className="text-right px-3">{r.average || "—"}</td>
              <td className="text-right px-3">{r.finish_average || "—"}</td>
              <td className="text-right px-3">{r.high_finish || "—"}</td>
              <td className="text-right px-4 font-bold text-white text-base">{r.points}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={hasHistoricDraws ? 13 : 12} className="py-10 text-center" style={{ color: "var(--dc-muted)" }}>Keine Daten.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
